import React, { useState } from 'react';
import {
    View, Text, StyleSheet, FlatList, TouchableOpacity,
    Image, Modal, TextInput, Platform, ActivityIndicator
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import UserAvatar from '../../../components/social/shared/UserAvatar';
import { getMediaUrl } from '../../../utils/media';

const GroupInfoScreen = ({ route, navigation }) => {
    const { room } = route.params;
    const insets = useSafeAreaInsets();
    const [modalVisible, setModalVisible] = useState(false);
    const [newParticipantId, setNewParticipantId] = useState('');

    const renderMember = ({ item }) => (
        <View style={styles.memberItem}>
            <UserAvatar source={item.user?.profileImageUrl} name={item.user?.name} size={42} />
            <View style={styles.memberInfo}>
                <Text style={styles.memberName}>{item.user?.name}</Text>
                <Text style={styles.memberRole}>{item.role === 'admin' ? 'Community Leader' : 'Member'}</Text>
            </View>
            {item.role === 'admin' && (
                <View style={styles.adminBadge}>
                    <Ionicons name="shield-checkmark" size={12} color="#0891b2" />
                    <Text style={styles.adminBadgeText}>Admin</Text>
                </View>
            )}
        </View>
    );

    return (
        <View style={styles.container}>
            {/* Social Header */}
            <View style={[styles.headerWrapper, { paddingTop: insets.top }]}>
                <LinearGradient
                    colors={['#0c4a6e', '#0369a1', '#0891b2']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.orbLarge} />
                <View style={styles.headerContent}>
                    <TouchableOpacity
                        style={styles.backBtn}
                        onPress={() => navigation.goBack()}
                    >
                        <Ionicons name="arrow-back" size={22} color="rgba(255,255,255,0.9)" />
                    </TouchableOpacity>
                    <View style={styles.headerCenter}>
                        <Text style={styles.headerTitle}>Group Info</Text>
                        <Text style={styles.headerSubtitle}>Community Details</Text>
                    </View>
                </View>

                <View style={styles.groupMainInfo}>
                    <View style={styles.avatarWrapper}>
                        {room.avatarUrl ? (
                            <Image source={{ uri: getMediaUrl(room.avatarUrl) }} style={styles.avatar} />
                        ) : (
                            <View style={[styles.avatar, styles.avatarPlaceholder]}>
                                <Ionicons name="people" size={40} color="#FFFFFF" />
                            </View>
                        )}
                        <TouchableOpacity style={styles.editAvatarBtn}>
                            <Ionicons name="camera" size={16} color="#FFFFFF" />
                        </TouchableOpacity>
                    </View>
                    <Text style={styles.groupNameText}>{room.name || 'Group Chat'}</Text>
                    <View style={styles.memberCountBadge}>
                        <Text style={styles.memberCountText}>{room.participants?.length} Members</Text>
                    </View>
                </View>
            </View>

            <View style={styles.content}>
                <View style={styles.sectionHeader}>
                    <Text style={styles.sectionTitle}>Participants</Text>
                    <TouchableOpacity style={styles.addBtn} onPress={() => setModalVisible(true)}>
                        <Ionicons name="person-add" size={18} color="#0891b2" />
                        <Text style={styles.addBtnText}>Add</Text>
                    </TouchableOpacity>
                </View>

                <FlatList
                    data={room.participants}
                    renderItem={renderMember}
                    keyExtractor={item => item.id.toString()}
                    contentContainerStyle={styles.memberList}
                    showsVerticalScrollIndicator={false}
                />
            </View>

            {/* Modal remains same for now but styled */}
            <Modal
                animationType="slide"
                transparent={true}
                visible={modalVisible}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={styles.modalOverlay}>
                    <View style={styles.modalContent}>
                        <Text style={styles.modalTitle}>Add Participant</Text>
                        <TextInput
                            style={styles.modalInput}
                            placeholder="Enter User ID or User Name"
                            value={newParticipantId}
                            onChangeText={setNewParticipantId}
                        />
                        <View style={styles.modalActions}>
                            <TouchableOpacity style={styles.modalCancel} onPress={() => setModalVisible(false)}>
                                <Text style={styles.modalCancelText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.modalAdd} onPress={() => setModalVisible(false)}>
                                <Text style={styles.modalAddText}>Add Member</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F0FDFF',
    },
    headerWrapper: {
        overflow: 'hidden',
        paddingBottom: 24,
    },
    headerContent: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingTop: Platform.OS === 'ios' ? 12 : 20,
        paddingBottom: 12,
        gap: 12,
    },
    backBtn: {
        width: 38,
        height: 38,
        borderRadius: 11,
        backgroundColor: 'rgba(255,255,255,0.12)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerCenter: {
        flex: 1,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#FFFFFF',
    },
    headerSubtitle: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.6)',
        fontWeight: '500',
        textTransform: 'uppercase',
    },
    orbLarge: {
        position: 'absolute',
        width: 150,
        height: 150,
        borderRadius: 75,
        backgroundColor: 'rgba(34,211,238,0.1)',
        right: -40,
        top: -20,
    },
    groupMainInfo: {
        alignItems: 'center',
        marginTop: 10,
    },
    avatarWrapper: {
        position: 'relative',
        marginBottom: 12,
    },
    avatar: {
        width: 100,
        height: 100,
        borderRadius: 50,
        borderWidth: 3,
        borderColor: 'rgba(255,255,255,0.3)',
    },
    avatarPlaceholder: {
        backgroundColor: 'rgba(255,255,255,0.15)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    editAvatarBtn: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        backgroundColor: '#0891b2',
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 2,
        borderColor: '#0369a1',
    },
    groupNameText: {
        fontSize: 22,
        fontWeight: '800',
        color: '#FFFFFF',
        marginBottom: 6,
    },
    memberCountBadge: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 12,
    },
    memberCountText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '600',
    },
    content: {
        flex: 1,
        marginTop: -20,
        backgroundColor: '#F0FDFF',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
    },
    addBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        backgroundColor: 'rgba(6,182,212,0.08)',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 10,
    },
    addBtnText: {
        fontSize: 14,
        color: '#0891b2',
        fontWeight: '700',
    },
    memberList: {
        paddingBottom: 20,
    },
    memberItem: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        padding: 12,
        borderRadius: 14,
        marginBottom: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.04,
        shadowRadius: 2,
        elevation: 1,
    },
    memberInfo: {
        flex: 1,
        marginLeft: 12,
    },
    memberName: {
        fontSize: 15,
        fontWeight: '700',
        color: '#111827',
    },
    memberRole: {
        fontSize: 12,
        color: '#6B7280',
        marginTop: 2,
    },
    adminBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: 'rgba(6,182,212,0.1)',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    },
    adminBadgeText: {
        fontSize: 10,
        fontWeight: '800',
        color: '#0891b2',
        textTransform: 'uppercase',
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        padding: 20,
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: 20,
        padding: 24,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 16,
    },
    modalInput: {
        backgroundColor: '#F3F4F6',
        borderRadius: 12,
        padding: 14,
        fontSize: 15,
        marginBottom: 20,
    },
    modalActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
    modalCancel: {
        paddingVertical: 10,
        paddingHorizontal: 16,
    },
    modalCancelText: {
        color: '#6B7280',
        fontWeight: '700',
    },
    modalAdd: {
        backgroundColor: '#0891b2',
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 10,
    },
    modalAddText: {
        color: '#FFFFFF',
        fontWeight: '700',
    },
});

export default GroupInfoScreen;
